// Studio — the canvas over the workspace (Studio v0; pen B-02..B-06, B4 and
// "Studio – detail design"). Pure: cards, where they sit, which are selected,
// folded or taken off the canvas are projected in (studioProps.ts); every
// change goes out as an intent (select, move, open, put in a container, fold,
// remove from the canvas, delete, upload, new material).
//
// Local state is view-ephemeral only (none of it survives a reload): the zoom,
// the pan offset, the active tool, an open menu, and a gesture in progress
// (useStudioGesture.ts).
//
// Input, with no global listener and no chords (CLAUDE.md, desktop-only
// chords): pointer gestures (useStudioGesture.ts) move a card, drop it on
// another, pan, or draw the Container tool's box; the wheel pans and
// ctrl/⌘+wheel (a trackpad pinch) zooms about the pointer
// (useStudioViewport.ts); double-click opens (on a name: renames it, as a
// click on the selected card's name does; MF-1, studioMachine.ts, whose state
// and events come in and go out as `machine` and `onEvent`); right-click opens the card's
// menu, or on the empty canvas the canvas menu, at the pointer
// (studioCardMenu.tsx). With a card focused, its plain keys (studioKeys.ts):
// arrows move it, Enter opens, Space selects, Delete takes it off the canvas,
// Esc clears the selection. While the canvas has focus (and no field or menu
// does), the toolbar's single letters pick a tool, Esc drops a gesture or
// clears the selection, and ↑/↓ switch a pending drop's kind
// (studioTools.ts). Tab walks the cards in reading order. One selected
// container gets the module toolbar above it (ModuleToolbar.tsx). A new card
// goes in the free spot nearest where it was made (studioLayout.ts).

export { Studio };

import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import {
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import type { ContainerKind } from "../../app/containerFacts.js";
import { Button } from "../Button/Button.js";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { Icon } from "../Icon/Icon.js";
import type { MenuItem } from "../Menu/Menu.js";
import type { PartIntent } from "../ModuleCard/partOps.js";
import { StudioAsk } from "../StudioAsk/StudioAsk.js";
import { StudioFind } from "../StudioFind/StudioFind.js";
import { StudioToolbar } from "../StudioToolbar/StudioToolbar.js";
import { keyIntent, type Tool } from "../StudioToolbar/studioTools.js";
import type { CardNaming } from "./CardName.js";
import { ModuleToolbar } from "./ModuleToolbar.js";
import { StudioCardSlot } from "./StudioCardSlot.js";
import { StudioPointMenu } from "./StudioPointMenu.js";
import { StudioWorkLayer } from "./StudioWorkLayer.js";
import { StudioZoom } from "./StudioZoom.js";
import { readingOrder } from "./studioBox.js";
import { useStudioMenus } from "./studioCardMenu.js";
import type { StudioCard as Card, ContainerCard } from "./studioCards.js";
import { openCommands } from "./studioDesk.js";
import { dropTargetOf } from "./studioDrop.js";
import type { FindItem } from "./studioFind.js";
import { cardKeyIntent } from "./studioKeys.js";
import {
    freeSpotNear,
    initialView,
    newCardSize,
    nudge,
    type Point,
    restoreView,
    type Size,
    type StudioView,
    zoomAround,
    zoomIn,
    zoomOut,
} from "./studioLayout.js";
import type { StudioProps } from "./studioProps.js";
import { useStudioFind } from "./useStudioFind.js";
import { useStudioGesture } from "./useStudioGesture.js";
import { useStudioViewport } from "./useStudioViewport.js";
import "./Studio.css";

// A menu opened at the pointer (viewport pixels): a card's, or the canvas's
// (things made at `point`, canvas units).
type PointMenu =
    | { readonly type: "card"; readonly at: Point; readonly id: string }
    | { readonly type: "canvas"; readonly at: Point; readonly point: Point };

// The module toolbar's gap above its container (pen: 12).
const TOOLBAR_GAP = 12;

// What owns its own keys: a field, or an open menu (portalled, but its key
// events still bubble here through React).
const KEY_OWNERS = "input, textarea, select, [contenteditable], [role='menu']";

const Studio = ({
    cards,
    placements,
    selected,
    projectTitle,
    onAllProjects,
    loading,
    onSelectOnly,
    onToggleSelected,
    onClearSelection,
    onExport,
    onMove,
    onPutInNewModule,
    onPutInNewCourse,
    onAddToContainer,
    onPartIntent,
    machine,
    onEvent,
    folded,
    onToggleFold,
    onRemoveFromCanvas,
    offCanvas,
    onPutBack,
    onDelete,
    revealed,
    containerKind,
    onContainerKindChange,
    onUpload,
    onNewMaterial,
    menuShown,
    onToggleMenu,
    online,
    work,
    onOpenWork,
    onAsk,
    storedView,
    onLeave,
    onOpen,
    modeSwitch,
    exportCheck,
}: StudioProps): ReactNode => {
    const { t } = useLingui();
    const [view, setView] = useState<StudioView>(initialView);
    const { zoom, pan } = view;
    const [tool, setTool] = useState<Tool>("select");
    const [materialMenuOpen, setMaterialMenuOpen] = useState(false);
    const [pointMenu, setPointMenu] = useState<PointMenu | null>(null);
    // The card whose Ask prompt is open (StudioAsk.tsx).
    const [asking, setAsking] = useState<string | null>(null);
    const viewport = useRef<HTMLDivElement>(null);
    const root = useRef<HTMLDivElement>(null);

    // The buttons zoom about the viewport's centre, so what is in the middle
    // stays there.
    const zoomTo = (next: number) => {
        const box = viewport.current?.getBoundingClientRect();
        const c = { x: (box?.width ?? 0) / 2, y: (box?.height ?? 0) / 2 };
        setView((v) => zoomAround(v, c, next));
    };
    const sizeOf = (): Size => ({
        width: viewport.current?.clientWidth ?? 0,
        height: viewport.current?.clientHeight ?? 0,
    });
    // Fit to content: every card in sight.
    const fitView = () => setView(restoreView(null, placements, sizeOf()));

    // Open where this person left off, once the cards are known (else every
    // card fitted in), before the first paint so the view does not jump.
    const restored = useRef(false);
    // biome-ignore lint/correctness/useExhaustiveDependencies: restores once, when the cards first arrive; later card changes must not move the view.
    useLayoutEffect(() => {
        if (restored.current || loading) return;
        restored.current = true;
        setView(restoreView(storedView, placements, sizeOf()));
    }, [loading]);

    useStudioViewport({ viewport, view, setView, sizeOf, restored, onLeave });

    // A card comes to the middle of the view, at the same zoom.
    const flyTo = (id: string) => {
        const p = placements.get(id);
        if (p === undefined) return;
        const { width, height } = sizeOf();
        setView((v) => ({
            zoom: v.zoom,
            pan: {
                x: width / 2 - (p.x + p.width / 2) * v.zoom,
                y: height / 2 - (p.y + p.height / 2) * v.zoom,
            },
        }));
    };
    // A revealed card (an upload already on the canvas) comes into view.
    // biome-ignore lint/correctness/useExhaustiveDependencies: moves the view once per reveal, not whenever the cards change.
    useEffect(() => {
        if (revealed !== null) flyTo(revealed.id);
    }, [revealed?.nonce]);
    const find = useStudioFind(cards);

    // Opening goes to the Editor, at it (PS-56); `part`: one part of a
    // container card, opened from it.
    const openOnDesk = (card: Card, part?: string) =>
        onOpen(...openCommands(card, part));
    // A match picked in Find: the desk flies to its card and selects it; a
    // match inside a container opens the binder it lies in (a module of a
    // course: that module).
    const pickFound = (hit: FindItem) => {
        find.setOpen(false);
        flyTo(hit.card);
        onSelectOnly(hit.card);
        const card = cards.find((c) => c.id === hit.card);
        if (card === undefined || hit.inside.length === 0) return;
        openOnDesk(card, hit.kind === "module" ? hit.id : hit.inside[1]?.id);
    };
    const titleOf = (ids: readonly string[]): string =>
        cards.find((c) => c.id === ids[0])?.title ?? "";
    const removeFromCanvas = (ids: readonly string[]) =>
        onRemoveFromCanvas(ids, titleOf(ids));

    // A new container goes in the free spot nearest `at`; the cards going
    // into it (all but sources, which stay on the canvas) leave theirs.
    const putInNew = (kind: ContainerKind, ids: readonly string[], at: Point) =>
        (kind === "module" ? onPutInNewModule : onPutInNewCourse)(
            ids,
            freeSpotNear(
                at,
                newCardSize(ids.length),
                placements,
                new Set(
                    cards
                        .filter(
                            (c) =>
                                ids.includes(c.id) &&
                                (c.type === "material" ||
                                    c.type === "container"),
                        )
                        .map((c) => c.id),
                ),
            ),
        );
    const selectedIds = cards
        .filter((c) => selected.has(c.id))
        .map((c) => c.id);

    const g = useStudioGesture({
        viewport,
        root,
        view,
        setView,
        tool,
        setTool,
        cards,
        placements,
        folded,
        containerKind,
        intents: {
            onToggleFold,
            putInNew,
            onMove,
            onAddToContainer,
            onToggleSelected,
            onSelectOnly,
            onClearSelection,
            onNameClicked: (id) => onEvent({ type: "name_clicked", id }),
        },
    });
    const { gesture, drop } = g;

    // A new material: next to the selection, else in the middle of the view.
    const newMaterialAt = (): Point => {
        const first = placements.get(selectedIds[0] ?? "");
        const size = newCardSize(null);
        const { width, height } = sizeOf();
        const centre = g.toCanvas({ x: width / 2, y: height / 2 });
        return freeSpotNear(
            first ?? {
                x: centre.x - size.width / 2,
                y: centre.y - size.height / 2,
            },
            size,
            placements,
        );
    };
    // A part taken out of a container comes back as its own card, in the
    // free spot nearest the container's right side.
    const onPart = (container: ContainerCard, intent: PartIntent) => {
        if (intent.type === "open") {
            openOnDesk(container, intent.part);
            return;
        }
        const at = placements.get(container.id);
        if (intent.type === "remove" && at !== undefined)
            onMove(
                intent.part,
                freeSpotNear(
                    { x: at.x + at.width, y: at.y },
                    newCardSize(null),
                    placements,
                ),
            );
        onPartIntent(container, intent);
    };
    // Double-click on the name renames it (MF-1); elsewhere it opens the
    // part under the pointer, else the card. The card holds the pointer from
    // its press (a drag may follow), so the browser aims the double-click at
    // the card, not at the name or the part row inside it.
    const onDoubleOpen = (card: Card, e: MouseEvent) => {
        const under = document.elementFromPoint(e.clientX, e.clientY);
        if (under?.closest("[data-name]") != null) {
            onEvent({ type: "name_double_clicked", id: card.id });
            return;
        }
        const part =
            card.type === "container"
                ? under?.closest<HTMLElement>("[data-part-id]")?.dataset.partId
                : undefined;
        openOnDesk(card, part);
    };
    // The cards in reading order; the new container's card goes where the
    // first one sits.
    const putIn = (kind: ContainerKind, ids: readonly string[]) => {
        const ordered = readingOrder(ids, placements);
        const first = placements.get(ordered[0] ?? "");
        if (first === undefined) return;
        putInNew(kind, ordered, { x: first.x, y: first.y });
    };
    // The Container tool (of the chosen kind): wraps the selection if there
    // is one (then back to Select), else waits for a box.
    const onContainer = (kind: ContainerKind) => {
        if (selectedIds.length > 0) {
            putIn(kind, selectedIds);
            setTool("select");
        } else setTool("container");
    };
    const menus = useStudioMenus({
        cards,
        selectedIds,
        placements,
        offCanvas,
        online,
        intents: {
            onOpen: (card) => openOnDesk(card),
            onRename: (card) => onEvent({ type: "rename_chosen", id: card.id }),
            onExport,
            putIn,
            onAddToContainer,
            onRemoveFromCanvas: removeFromCanvas,
            onDelete,
            onNewModuleAt: (at) => putInNew("module", [], at),
            onUpload,
            onPutBack,
        },
    });

    // The toolbar's single letters, Esc and ↑/↓, on the canvas only
    // (studioTools.ts).
    const onKey = (e: KeyboardEvent<HTMLElement>) => {
        const target = e.target;
        const intent = keyIntent(
            {
                key: e.key,
                modified: e.metaKey || e.ctrlKey || e.altKey,
                inTextField:
                    target instanceof Element &&
                    target.closest(KEY_OWNERS) !== null,
            },
            {
                tool,
                gestureActive: gesture !== null,
                selectedCount: selectedIds.length,
                online,
                combining: drop.type === "new_container",
            },
        );
        if (intent === null) return;
        e.preventDefault();
        switch (intent.type) {
            case "pick":
                setTool(intent.tool);
                return;
            case "wrap-selection":
                onContainer(containerKind);
                return;
            case "upload":
                onUpload();
                return;
            case "open-material-menu":
                setMaterialMenuOpen(true);
                return;
            case "find":
                find.setOpen(true);
                return;
            case "cancel":
                g.cancel();
                setTool("select");
                return;
            case "clear-selection":
                onClearSelection();
                return;
            case "switch-kind":
                g.switchKind();
                return;
            default:
                exhaustSwitchCase(intent);
        }
    };

    // Right-click: the card's menu, or on the empty canvas the canvas's,
    // where the pointer is.
    const onContextMenu = (e: MouseEvent<HTMLDivElement>) => {
        const target = e.target;
        if (!(target instanceof Element)) return;
        if (!e.currentTarget.contains(target)) return;
        e.preventDefault();
        const at = g.localOf(e);
        const id = target
            .closest<HTMLElement>("[data-card-id]")
            ?.getAttribute("data-card-id");
        setPointMenu(
            id === null || id === undefined
                ? { type: "canvas", at, point: g.toCanvas(at) }
                : { type: "card", at, id },
        );
    };

    const onCardKey = (card: Card, e: KeyboardEvent<HTMLDivElement>) => {
        const intent = cardKeyIntent({
            key: e.key,
            shift: e.shiftKey,
            modified: e.metaKey || e.ctrlKey || e.altKey,
            dragging: gesture !== null,
            anySelected: selected.size > 0,
        });
        if (intent === null) return;
        e.preventDefault();
        // The card's own key: the canvas's must not act on it too.
        e.stopPropagation();
        switch (intent.type) {
            case "nudge": {
                const at = placements.get(card.id);
                if (at !== undefined)
                    onMove(card.id, nudge(at, intent.direction, intent.step));
                return;
            }
            case "open":
                openOnDesk(card);
                return;
            case "toggle-selected":
                onToggleSelected(card.id);
                return;
            case "remove-from-canvas":
                // With the card selected, the whole selection.
                removeFromCanvas(
                    selected.has(card.id) ? selectedIds : [card.id],
                );
                // Focus stays on the canvas, so its keys keep working.
                root.current?.focus({ preventScroll: true });
                return;
            case "clear-selection":
                onClearSelection();
                return;
            default:
                exhaustSwitchCase(intent);
        }
    };

    const draggedId = gesture?.type === "card" ? gesture.id : null;
    const selectedCount = selectedIds.length;
    const dropTarget = dropTargetOf(drop);
    const inBox = new Set(g.boxed);
    const empty = !loading && cards.length === 0;
    // Tab walks the cards in reading order, and they are drawn in it too. A
    // dragged card rides above its neighbours by its z-index (Studio.css),
    // never by moving in the page: moving its node on the press cancelled
    // the click, so a double-click never opened a card that was not last.
    const byId = new Map(cards.map((c) => [c.id, c]));
    const ordered = readingOrder(
        cards.map((c) => c.id),
        placements,
    ).flatMap((id) => {
        const c = byId.get(id);
        return c === undefined ? [] : [c];
    });
    // What releasing a combining drop or the box makes.
    const releaseLabel = (n: number, kind: ContainerKind): string =>
        kind === "module"
            ? t`${plural(n, {
                  one: "Release to put # card in a new module",
                  other: "Release to put # cards in a new module",
              })}`
            : t`${plural(n, {
                  one: "Release to put # card in a new course",
                  other: "Release to put # cards in a new course",
              })}`;
    // The card whose name is being typed (MF-1).
    const renaming =
        machine.interaction.type === "renaming" ? machine.interaction : null;
    const namingOf = (id: string): CardNaming => ({
        view:
            renaming?.id === id
                ? {
                      type: "field",
                      origin: renaming.origin,
                      blank: renaming.blank,
                  }
                : { type: "name", failed: machine.notes.get(id)?.previous },
        onSubmit: (text) => onEvent({ type: "rename_submitted", text }),
        onCancel: () => onEvent({ type: "rename_cancelled" }),
    });
    // The one selected container: its toolbar floats above it.
    const [onlySelected] = selectedIds;
    const toolbarCard =
        selectedIds.length === 1 ? byId.get(onlySelected ?? "") : undefined;
    const toolbarAt =
        toolbarCard?.type === "container" &&
        gesture?.moved !== true &&
        renaming?.id !== toolbarCard.id
            ? placements.get(toolbarCard.id)
            : undefined;
    // The one selected card gets "Ask" (pen "click to add to context"),
    // while the desk is still and nothing else is in front.
    const askCard =
        toolbarCard !== undefined &&
        gesture === null &&
        renaming?.id !== toolbarCard.id &&
        !find.open
            ? toolbarCard
            : undefined;
    const askAt =
        askCard === undefined ? undefined : placements.get(askCard.id);
    const pointMenuItems = ((): readonly MenuItem[] => {
        if (pointMenu === null) return [];
        switch (pointMenu.type) {
            case "canvas":
                return menus.canvas(pointMenu.point);
            case "card": {
                const card = byId.get(pointMenu.id);
                return card === undefined
                    ? []
                    : menus.card(card, selected.has(card.id));
            }
            default:
                return exhaustSwitchCase(pointMenu);
        }
    })();

    return (
        <section
            ref={root}
            className="bilda-studio"
            aria-label={t`Project canvas`}
            tabIndex={-1}
            data-tool={tool}
            data-gesture={gesture?.moved ? gesture.type : undefined}
            onKeyDown={onKey}
        >
            {/* biome-ignore lint/a11y/noStaticElementInteractions: the canvas surface: pointer gestures and its right-click menu; every action also has a button or menu. */}
            <div
                ref={viewport}
                className="bilda-studio__viewport"
                onPointerDown={(e) => {
                    // A press on the desk ends Find; nothing else changes.
                    if (find.open) find.setOpen(false);
                    setAsking(null);
                    g.startPan(e);
                }}
                onPointerMove={g.onPointerMove}
                onPointerUp={g.onPointerUp}
                onPointerCancel={g.cancel}
                onContextMenu={onContextMenu}
            >
                <div
                    className="bilda-studio__plane"
                    style={{
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    }}
                >
                    {ordered.map((card) => {
                        const at = g.cardAt(card);
                        if (at === undefined) return null;
                        const isSelected = selected.has(card.id);
                        return (
                            <StudioCardSlot
                                key={card.id}
                                card={card}
                                menu={menus.card(card, isSelected)}
                                naming={namingOf(card.id)}
                                state={{
                                    at,
                                    width: placements.get(card.id)?.width,
                                    selected: isSelected,
                                    boxed: inBox.has(card.id),
                                    dropTarget: dropTarget === card.id,
                                    dragging:
                                        draggedId === card.id &&
                                        gesture?.moved === true,
                                    dropSlot:
                                        drop.type === "add_as_part" &&
                                        drop.container === card.id
                                            ? drop.number
                                            : undefined,
                                    folded: folded.has(card.id),
                                    online,
                                    revealed:
                                        revealed?.id === card.id
                                            ? revealed.nonce
                                            : undefined,
                                    found: find.lookOf(card.id),
                                }}
                                handlers={{
                                    onPointerDown: (e) => {
                                        // Assistant here: a press on a card
                                        // asks about it, then Select.
                                        if (tool !== "assistant") {
                                            g.startCardDrag(card, e);
                                            return;
                                        }
                                        // The card must not take the focus
                                        // from the prompt's field.
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onSelectOnly(card.id);
                                        setAsking(card.id);
                                        setTool("select");
                                    },
                                    onDoubleClick: (e) => onDoubleOpen(card, e),
                                    onKeyDown: (e) => onCardKey(card, e),
                                    onPart: (intent) =>
                                        card.type === "container"
                                            ? onPart(card, intent)
                                            : undefined,
                                    onToggleFold: () => onToggleFold(card.id),
                                }}
                            />
                        );
                    })}
                    <StudioWorkLayer
                        work={work}
                        anchored
                        onOpenWork={onOpenWork}
                    />
                </div>
                {g.screenBox === null ? null : (
                    <>
                        <div
                            className="bilda-studio__box"
                            style={{
                                left: g.screenBox.x,
                                top: g.screenBox.y,
                                width: g.screenBox.width,
                                height: g.screenBox.height,
                            }}
                        />
                        {g.boxed.length === 0 ? null : (
                            <div
                                className="bilda-studio__box-label"
                                style={{
                                    left: g.screenBox.x,
                                    top: g.screenBox.y + g.screenBox.height,
                                }}
                            >
                                {releaseLabel(g.boxed.length, containerKind)}
                            </div>
                        )}
                    </>
                )}
                {/* A card held over another (§8): what releasing makes; the
                    ▾ says ↑/↓ switch its kind. */}
                {drop.type === "new_container" && gesture?.type === "card" ? (
                    <div
                        className="bilda-studio__box-label"
                        style={{
                            left: gesture.hover.x * zoom + pan.x,
                            top: gesture.hover.y * zoom + pan.y + 16,
                        }}
                    >
                        {releaseLabel(2, drop.kind)}
                        <span
                            className="bilda-studio__switch-kind"
                            title={t`↑ or ↓ switches between module and course`}
                        >
                            <Icon name="chevron-down" size="xs" />
                        </span>
                    </div>
                ) : null}
            </div>

            {toolbarCard?.type !== "container" ||
            toolbarAt === undefined ? null : (
                <ModuleToolbar
                    card={toolbarCard}
                    at={{
                        x: pan.x + toolbarAt.x * zoom,
                        y: pan.y + toolbarAt.y * zoom - TOOLBAR_GAP,
                    }}
                    online={online}
                    onOpen={() => openOnDesk(toolbarCard)}
                    onPart={(intent) => onPart(toolbarCard, intent)}
                    onExport={() => onExport(toolbarCard)}
                    onUndo={
                        machine.undo?.id === toolbarCard.id
                            ? () =>
                                  onEvent({
                                      type: "undo_chosen",
                                      id: toolbarCard.id,
                                  })
                            : undefined
                    }
                />
            )}

            {askCard === undefined || askAt === undefined ? null : (
                <StudioAsk
                    key={askCard.id}
                    title={askCard.title}
                    quiz={
                        askCard.type === "material" &&
                        askCard.template === "quiz"
                    }
                    pill={{
                        // Clear of the card's "…" menu.
                        x: pan.x + (askAt.x + askAt.width) * zoom - 44,
                        y: pan.y + askAt.y * zoom,
                    }}
                    prompt={{
                        x: pan.x + askAt.x * zoom,
                        y: pan.y + (askAt.y + askAt.height) * zoom + 16,
                    }}
                    open={asking === askCard.id}
                    online={online}
                    onOpen={() => setAsking(askCard.id)}
                    onClose={() => {
                        setAsking(null);
                        root.current?.focus({ preventScroll: true });
                    }}
                    onSend={(text) => {
                        onAsk(text);
                        setAsking(null);
                    }}
                />
            )}

            {empty ? null : (
                <StudioFind
                    open={find.open}
                    text={find.text}
                    filter={find.filter}
                    hits={find.hits}
                    onOpen={() => find.setOpen(true)}
                    onClose={() => {
                        find.setOpen(false);
                        root.current?.focus({ preventScroll: true });
                    }}
                    onText={find.setText}
                    onFilter={find.setFilter}
                    onPick={pickFound}
                />
            )}

            {/* A turn with nothing to sit beside: in the middle of the view. */}
            <StudioWorkLayer
                work={work}
                anchored={false}
                onOpenWork={onOpenWork}
            />

            {empty && work.length === 0 ? (
                <div className="bilda-studio__empty">
                    <EmptyState
                        icon={<Icon name="upload" size="md" />}
                        title={t`Drop sources anywhere here`}
                        body={t`Files are saved in Uploaded sources and show up here as cards, next to the materials you make from them. Or tell the assistant what you need.`}
                    >
                        <Button variant="secondary" onClick={onUpload}>
                            {t`Upload sources`}
                            <Icon name="upload" />
                        </Button>
                    </EmptyState>
                </div>
            ) : null}

            <div className="bilda-studio__titlebar">
                <div className="bilda-studio__crumb">
                    <button
                        type="button"
                        className="bilda-studio__crumb-root"
                        onClick={onAllProjects}
                    >
                        {t`Projects`}
                    </button>
                    <span aria-hidden="true">/</span>
                    <span className="bilda-studio__crumb-name">
                        {projectTitle}
                    </span>
                </div>
                {modeSwitch}
            </div>

            <div className="bilda-studio__top-right">{exportCheck}</div>

            {selectedCount >= 2 ? (
                <div className="bilda-studio__selection">
                    <span className="bilda-studio__selection-count">
                        {t`${selectedCount} cards selected`}
                    </span>
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={!online}
                        onClick={() => putIn(containerKind, selectedIds)}
                    >
                        <Icon name={containerKind} />
                        {containerKind === "module"
                            ? t`Put in new module`
                            : t`Put in new course`}
                    </Button>
                </div>
            ) : null}

            <div className="bilda-studio__pills">
                <StudioZoom
                    zoom={zoom}
                    onZoomIn={() => zoomTo(zoomIn(zoom))}
                    onZoomOut={() => zoomTo(zoomOut(zoom))}
                    onZoomTo={zoomTo}
                    onFit={fitView}
                />
            </div>

            {pointMenu === null ? null : (
                <StudioPointMenu
                    at={pointMenu.at}
                    onClose={() => setPointMenu(null)}
                    items={pointMenuItems}
                />
            )}

            <StudioToolbar
                tool={tool}
                menuShown={menuShown}
                online={online}
                materialMenuOpen={materialMenuOpen}
                onMaterialMenuOpenChange={setMaterialMenuOpen}
                onToggleMenu={onToggleMenu}
                onPickTool={setTool}
                containerKind={containerKind}
                onContainer={(kind) => {
                    onContainerKindChange(kind);
                    onContainer(kind);
                }}
                onUpload={onUpload}
                onNewMaterial={(kind) => onNewMaterial(kind, newMaterialAt())}
            />
        </section>
    );
};
